/**
 * Generates a simple perceptual hash of an image using canvas.
 * Resizes to 8x8 grayscale and creates a 64-bit hash string.
 * Similar images produce similar hashes even with compression differences.
 */
export async function generateImageHash(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const size = 8;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;

      // Draw resized grayscale image
      ctx.drawImage(img, 0, 0, size, size);
      const imageData = ctx.getImageData(0, 0, size, size);
      const pixels = imageData.data;

      // Convert to grayscale values
      const grayscale: number[] = [];
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        grayscale.push(0.299 * r + 0.587 * g + 0.114 * b);
      }

      // Calculate average
      const avg = grayscale.reduce((a, b) => a + b, 0) / grayscale.length;

      // Build hash — 1 if pixel above average, 0 if below
      const hash = grayscale.map(v => (v >= avg ? '1' : '0')).join('');

      URL.revokeObjectURL(url);
      resolve(hash);
    };

    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Calculates Hamming distance between two hashes.
 * Lower = more similar. 0 = identical. >10 = likely different.
 */
export function hammingDistance(hash1: string, hash2: string): number {
  if (hash1.length !== hash2.length) return Infinity;
  let distance = 0;
  for (let i = 0; i < hash1.length; i++) {
    if (hash1[i] !== hash2[i]) distance++;
  }
  return distance;
}

/**
 * Threshold for considering two images "the same".
 * 0 = identical, up to 10 allows for minor compression differences.
 */
export const HASH_SIMILARITY_THRESHOLD = 10;

export function isSimilarImage(hash1: string, hash2: string): boolean {
  return hammingDistance(hash1, hash2) <= HASH_SIMILARITY_THRESHOLD;
}